"use client";

import { useState, useEffect, useRef } from "react";
import { Search, X, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { trpc } from "@/lib/trpc/client";
import Image from "next/image";
import Link from "next/link";
import type { SearchResult } from "@/server/routers/search";
import { Title } from '@/components/ui/Headings';

interface SearchFlyoverProps {
  isOpen: boolean;
  onClose: () => void;
}

const SearchFlyover = ({ isOpen, onClose }: SearchFlyoverProps) => {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const { i18n, t } = useTranslation();

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  // Focus input when flyover opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Search query
  const { data: results, isLoading } = trpc.search.searchAll.useQuery(
    {
      query: debouncedQuery,
      locale: i18n.language as "en" | "de" | "uk",
      limit: 20,
    },
    {
      enabled: debouncedQuery.length >= 2,
    }
  );

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      // Prevent body scroll when flyover is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleResultClick = () => {
    onClose();
    setQuery("");
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Flyover Menu */}
      <div className="fixed inset-x-0 top-0 z-50 bg-white shadow-xl max-h-[80vh] overflow-hidden flex flex-col">
        {/* Search Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="max-w-4xl mx-auto flex items-center gap-4">
            <Search className="text-gray-400 w-6 h-6 flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('search.placeholder')}
              className="flex-1 text-xl outline-none"
            />
            {isLoading && (
              <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close search"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-4xl mx-auto">
            {query.length < 2 && (
              <div className="text-center text-gray-500 py-12">
                <Search className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>{t('search.startTyping')}</p>
              </div>
            )}

            {query.length >= 2 && !isLoading && (!results || results.length === 0) && (
              <div className="text-center text-gray-500 py-12">
                <p>{t('search.noResults', { query })}</p>
                <p className="text-sm mt-2">{t('search.tryDifferent')}</p>
              </div>
            )}

            {results && results.length > 0 && (
              <div className="space-y-6">
                {/* Group results by type */}
                {["journey", "article", "page"].map((type) => {
                  const typeResults = results.filter((r) => r.type === type);
                  if (typeResults.length === 0) return null;

                  return (
                    <div key={type}>
                      <Title size="xs" className="font-semibold text-gray-500 uppercase tracking-wide mb-3">
                        {type === "journey" && t('search.journeys')}
                        {type === "article" && t('search.articles')}
                        {type === "page" && t('search.pages')}
                      </Title>
                      <div className="space-y-2">
                        {typeResults.map((result) => (
                          <SearchResultItem
                            key={`${result.type}-${result.id}`}
                            result={result}
                            onClick={handleResultClick}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

// Individual result item component
const SearchResultItem = ({
  result,
  onClick,
}: {
  result: SearchResult;
  onClick: () => void;
}) => {
  return (
    <Link
      href={result.url}
      onClick={onClick}
      className="block p-4 rounded-lg hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-200"
    >
      <div className="flex gap-4">
        {result.image && result.type === "journey" && (
          <div className="relative w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden">
            <Image
              src={result.image}
              alt={result.title}
              fill
              className="object-cover"
            />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-gray-900 mb-1 line-clamp-1">
            {result.title}
          </h4>
          {result.highlightedSnippet ? (
            <p
              className="text-sm text-gray-600 line-clamp-2"
              dangerouslySetInnerHTML={{ __html: result.highlightedSnippet }}
            />
          ) : result.description ? (
            <p className="text-sm text-gray-600 line-clamp-2">
              {result.description}
            </p>
          ) : null}
          {result.category && (
            <span className="inline-block mt-2 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
              {result.category}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};

export default SearchFlyover;
